import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { DownloadIcon, UploadIcon } from 'lucide-react';
import {
  FormActions,
  FormCancelButton,
  FormSubmitButton,
} from '../../Components/Form';
import { LocationSelect } from '../../Components/Select/LocationSelect';
import apiClient from '../../Services/ApiClient';
import apiClientFormData from '../../Services/ApiClientFormData';
import { useSnackbar } from '../../Context/SnackbarProvider';
import useGetCustomData from '../../Hooks/useGetCustomData';
import { COLORS } from '../../Styles/Colors';

const ImportStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border: 1px solid ${COLORS.lightGray};
  border-radius: 8px;
  background-color: #f8f9fa;
`;

const ImportStepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${COLORS.teal};
  color: ${COLORS.white};
  font-size: 0.85rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const StepTitle = styled.span`
  color: ${COLORS.darkGray};
  font-size: 1rem;
  font-weight: 600;
`;

const ImportInstructionText = styled.p`
  margin: 0;
  color: ${COLORS.darkGray};
  font-size: 0.95rem;
  line-height: 1.6;
  text-align: center;
`;

const ColumnList = styled.ul`
  margin: 0;
  padding: 12px 16px 12px 32px;
  background-color: ${COLORS.white};
  border-radius: 6px;
  border-left: 3px solid ${COLORS.orange};
  color: ${COLORS.darkGray};
  font-size: 0.875rem;
  line-height: 1.8;
`;

const TemplateDownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background-color: ${COLORS.teal};
  color: ${COLORS.white};
  border: none;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;

  &:hover {
    background-color: ${COLORS.darkTeal};
    transform: translateY(-2px);
  }
`;

const FileDropZone = styled.label<{ $hasFile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 28px 20px;
  border: 2px dashed ${({ $hasFile }) => ($hasFile ? COLORS.teal : COLORS.gray)};
  border-radius: 8px;
  background-color: ${COLORS.white};
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: ${COLORS.teal};
    background-color: ${COLORS.teal}08;
  }

  input {
    display: none;
  }
`;

const SelectedFileName = styled.span`
  color: ${COLORS.teal};
  font-size: 0.9rem;
  font-weight: 600;
  word-break: break-all;
  text-align: center;
`;

const ImportWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const ImportContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 560px;
`;

const ImageUploadText = styled.span`
  color: ${COLORS.darkGray};
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
`;

const ImageUploadSubtext = styled.span`
  color: ${COLORS.lightGray};
  font-size: 0.75rem;
  text-align: center;
`;

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

const UserBulkImportComponent: React.FC<Props> = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [selectedOvs, setSelectedOvs] = useState<string>('');
  const [importing, setImporting] = useState(false);

  const [ovs] = useGetCustomData('/api/location/user/ovs');
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    if (ovs.length > 0 && !selectedOvs) {
      setSelectedOvs(ovs[0].OVID);
    }
  }, [ovs, selectedOvs]);

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient('/api/users/download-template');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'User-Import-Template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      showSnackbar('Failed to download template', 'error');
    }
  };

  const handleImport = async () => {
    if (!file) {
      showSnackbar('Please select a file', 'error');
      return;
    }
    if (!selectedOvs) {
      showSnackbar('Please select a location', 'error');
      return;
    }

    setImporting(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('OVID', selectedOvs);

    try {
      const response = await apiClientFormData('/api/users/import', { method: 'POST', body: formData });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to import users');
      }

      const result = await response.json();
      showSnackbar(`Successfully imported ${result.imported} user(s)`, 'success');
      if (result.imported > 0) {
        onSuccess();
        onClose();
      }
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to import users', 'error');
    } finally {
      setImporting(false);
    }
  };

  return (
    <ImportWrapper>
      <ImportContent>
        <ImportInstructionText>
          Import multiple users at once using an Excel spreadsheet.
          Download the template first to ensure your file has the correct column format.
          Row 2 of the template explains each column — enter data starting on row 3.
        </ImportInstructionText>

        <ColumnList>
          <li><strong>Username</strong> — required; unique login name</li>
          <li><strong>First Name</strong> — required</li>
          <li><strong>Last Name</strong> — required</li>
          <li><strong>Email</strong> — required; unique; used for Forgot Password</li>
          <li><strong>Mobile Number</strong> — optional</li>
          <li><strong>RFID</strong> — optional; alphanumeric password for the AIMS mobile app</li>
          <li><strong>User Type</strong> — optional legacy permission code (prefer Role)</li>
          <li><strong>Role</strong> — required; select from dropdown in the template</li>
        </ColumnList>

        <ImportStep>
          <ImportStepHeader>
            <StepNumber>1</StepNumber>
            <StepTitle>Download the template</StepTitle>
          </ImportStepHeader>
          <TemplateDownloadButton type="button" onClick={handleDownloadTemplate}>
            <DownloadIcon size={18} />
            Download Excel Template
          </TemplateDownloadButton>
        </ImportStep>

        <ImportStep>
          <ImportStepHeader>
            <StepNumber>2</StepNumber>
            <StepTitle>Select location for imported users</StepTitle>
          </ImportStepHeader>
          <LocationSelect
            ovs={ovs}
            selectedOvs={selectedOvs}
            onChange={setSelectedOvs}
            required
          />
        </ImportStep>

        <ImportStep>
          <ImportStepHeader>
            <StepNumber>3</StepNumber>
            <StepTitle>Upload your completed file</StepTitle>
          </ImportStepHeader>
          <FileDropZone $hasFile={!!file}>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <UploadIcon size={28} color={COLORS.teal} />
            {file ? (
              <SelectedFileName>{file.name}</SelectedFileName>
            ) : (
              <>
                <ImageUploadText>Click to select an .xlsx file</ImageUploadText>
                <ImageUploadSubtext>Only Excel (.xlsx) files are accepted</ImageUploadSubtext>
              </>
            )}
          </FileDropZone>
        </ImportStep>

        <FormActions onCancel={onClose}>
          <FormCancelButton type="button" onClick={onClose}>Cancel</FormCancelButton>
          <FormSubmitButton
            type="button"
            onClick={handleImport}
            disabled={!file || !selectedOvs || importing}
          >
            {importing ? 'Importing...' : 'Import Users'}
          </FormSubmitButton>
        </FormActions>
      </ImportContent>
    </ImportWrapper>
  );
};

export default UserBulkImportComponent;
